import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../actions/AuthActions";
import { Truck, LogIn, AlertCircle, Eye, EyeOff } from "lucide-react";

const LoginForm = () => {
  const [serviceNo, setServiceNo] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, isAuthenticated, msg } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (
      msg &&
      (msg.toLowerCase().includes("success") ||
        msg.toLowerCase().includes("successful"))
    ) {
      alert(msg);
    }
  }, [msg]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");

    if (!serviceNo.trim() || !password.trim()) {
      setLocalError("Service Number and Password are required");
      return;
    }

    try {
      await dispatch(loginUser(serviceNo, password));
    } catch (error) {
      setLocalError("Login failed. Please try again.");
    }
  };

  const isSuccessMessage =
    msg &&
    (msg.toLowerCase().includes("success") ||
      msg.toLowerCase().includes("successful"));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      {/* Glass Card */}
      <div className="relative w-full max-w-md">
        {/* Decorative Elements */}
        <div className="absolute -top-10 -left-10 w-32 h-32 bg-gray-200/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-gray-300/20 rounded-full blur-3xl"></div>
        
        {/* Main Card */}
        <div className="relative bg-white/80 backdrop-blur-md rounded-2xl shadow-xl border border-white/30 p-6 sm:p-8">
          {/* Logo & Header */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center shadow-lg">
                <Truck size={28} className="text-white" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-1">
              Aero View
            </h1>
            <p className="text-xs text-gray-500">Login to your account</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Service Number */}
            <div>
              <label
                htmlFor="serviceNo"
                className="block text-xs font-medium text-gray-700 mb-1.5"
              >
                Service Number
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="serviceNo"
                  value={serviceNo}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val) && val.length <= 7) {
                      setServiceNo(val);
                    }
                  }}
                  placeholder="Enter your service number"
                  disabled={loading}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg 
                    focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 
                    disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-xs font-medium text-gray-700 mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={loading}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg 
                    focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 
                    disabled:bg-gray-50 disabled:text-gray-500 transition-all pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {((msg && !isSuccessMessage) || localError) && (
              <div className="flex items-start gap-2 p-2.5 bg-red-50 border border-red-100 rounded-lg">
                <AlertCircle size={12} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">
                  {msg && !isSuccessMessage ? msg : localError}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg 
                hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed 
                flex items-center justify-center gap-2 mt-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Logging in...
                </>
              ) : (
                <>
                  <LogIn size={14} />
                  Login
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="text-center mt-6 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400">Authorized Users Only</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;